// Phase 9 R4 — Return previously-consumed stock to inventory via R1 RPC.
// Backend atomically: increments quantity_on_hand, decrements parts_line.inventory_consumed_qty,
// writes a `return_to_stock` ledger entry. Does NOT silently re-reserve.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function useReturnConsumedToStock() {
  const { activeShop } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      partsLineId,
      qty,
    }: {
      partsLineId: string;
      qty: number;
      ticketId?: string;
      inventoryItemId?: string;
    }) => {
      const { data, error } = await supabase.rpc('return_consumed_to_stock', {
        p_parts_line_id: partsLineId,
        p_qty: qty,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      const shopId = activeShop?.id;
      if (vars.ticketId) {
        qc.invalidateQueries({ queryKey: ['parts-lines', shopId, vars.ticketId] });
      }
      if (vars.inventoryItemId) {
        qc.invalidateQueries({ queryKey: ['inventory-item', shopId, vars.inventoryItemId] });
        qc.invalidateQueries({ queryKey: ['inventory-transactions', shopId, vars.inventoryItemId] });
      }
      qc.invalidateQueries({ queryKey: ['inventory-items', shopId] });
      toast.success(`Returned ${vars.qty}`);
    },
    onError: (e: any) => toast.error(e?.message ?? 'Return failed'),
  });
}
